import { createSelector } from 'reselect';
import {
  flow,
  get,
  omit,
  omitBy,
  isEqual,
  keys,
  extend,
  difference,
} from 'lodash/fp';
import {
  replace as replaceRoute,
  push as pushRoute,
} from 'connected-react-router';
import { getSearchUrl } from 'helpers/namedUrls';

import {
  formToQuery,
  formScaffolding,
  initSearchLayout,
  parseAdsSearch,
  stringifyAdsQuery,
  formFromMeta,
} from 'components/searchAds';

import { selectCarMakeById } from 'store/ducks/manufacturers';
import mapStateToProps from '../mapStateToProps';
import { getCmsResult } from '../../../selectors';

// These are always re-derived from the current vehicle selection, so they
// must never be blacklisted, or changing sort/page after landing on a
// make/model SEO page (e.g. via an "Alle Marken" link) silently drops it.
const VEHICLE_QUERY_KEYS = ['brand', 'model', 'subType'];

const getNextLocation = (
  carMakeById,
  form,
  pathname,
  blackListQueryKeys = [],
) => {
  const query = flow(
    omitBy((value, key) => isEqual(formScaffolding[key], value)),
    formToQuery(carMakeById),
    omit(difference(blackListQueryKeys, VEHICLE_QUERY_KEYS)),
  )(form);

  return {
    pathname,
    search: stringifyAdsQuery(query),
    query,
  };
};

const getCmsQuery = createSelector(
  getCmsResult,
  selectCarMakeById,
  (cmsResult, carMakeById) =>
    flow(
      get(['link-meta']),
      formFromMeta(),
      formToQuery(carMakeById),
    )(cmsResult),
);

export const beforeExtraFiltersOpened = (search) =>
  function* ({ getState }) {
    search = flow(
      getCmsQuery,
      extend(parseAdsSearch(search)),
      stringifyAdsQuery,
    )(getState());

    yield pushRoute({ pathname: getSearchUrl(), search });

    return { isRedirected: true };
  };

export const changeFilters = (form) =>
  function* ({ getState }) {
    const carMakeById = selectCarMakeById(getState());
    const nextLocation = getNextLocation(carMakeById, form, getSearchUrl());

    yield pushRoute(nextLocation);

    return nextLocation;
  };

export const changePage = (form) =>
  function* ({ getState, getCurrentLocation }) {
    const state = getState();
    const carMakeById = selectCarMakeById(state);
    const cmsQuery = getCmsQuery(state);
    const nextLocation = getNextLocation(
      carMakeById,
      form,
      getCurrentLocation().pathname,
      keys(cmsQuery),
    );

    yield pushRoute(nextLocation);

    return nextLocation;
  };

export const changeSort = (form) =>
  function* ({ getState, getCurrentLocation }) {
    const state = getState();
    const carMakeById = selectCarMakeById(state);
    const cmsQuery = getCmsQuery(state);
    const nextLocation = getNextLocation(
      carMakeById,
      form,
      getCurrentLocation().pathname,
      keys(cmsQuery),
    );

    yield replaceRoute(nextLocation);

    return nextLocation;
  };

export const fetch = (locals) =>
  function* ({ getState, getCurrentLocation }) {
    const routeParams = get(['params', 'route'], locals);

    const props = mapStateToProps(getState(), {
      location: getCurrentLocation(),
    });

    yield initSearchLayout(props, routeParams);
  };
